import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { EyeIcon } from 'lucide-react';
import Sidebar from '@/components/sidebar';
import HotTable from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import { getBaseTableSettings } from './config/handsontable';
import { useCompanyProject } from './hooks/useCompanyProject';
import { useProjectShipmentData } from './hooks/useProjectShipmentData';
import { useClientCompanies, useCreateShareLink } from '@/core/api/queries';
import { useUIState } from '@/core/UIStateProvider';
import ColumnCheckbox from './components/share-table-wizard/columnCheckbox';
import ClientSelect from './components/share-table-wizard/clientSelect';
import ExpirationDateSelect from './components/share-table-wizard/expirationDateSelect';
import Footer from './components/share-table-wizard/footer';
import { useSearchParams } from 'react-router-dom';

registerAllModules();

interface ShareTableWizardProps {
  isOpen: boolean;
  onClose: () => void;
  columns: any[];
  tableData: any[];
}

const INTERNAL_COLUMNS = ['internalReference', 'bookingNumber', 'bolNumber'];

const ShareTableWizard: React.FC<ShareTableWizardProps> = ({ isOpen, columns, tableData, onClose }) => {
  const [searchParams] = useSearchParams();
  const { project, projectId } = useCompanyProject();
  const { shipments } = useProjectShipmentData(projectId, false, searchParams);

  const { addNotification } = useUIState();

  const [selectedClient, setSelectedClient] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<any[]>([]);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [selectedExpirationDate, setSelectedExpirationDate] = useState<string>('never');

  const { mutateAsync: createShareLink, isPending: isCreatingShareLink } = useCreateShareLink();

  const uniqueClients = useMemo(() => {
    return Array.from(new Set(shipments?.map((shipment: any) => shipment.clientId))) as string[];
  }, [shipments]);

  const { data: clientCompanies, isLoading: isLoadingClients } = useClientCompanies(uniqueClients);

  const getExpirationDate = (expirationType: string): Date => {
    const now = new Date();
    switch (expirationType) {
      case '1hour':
        return new Date(now.getTime() + 60 * 60 * 1000);
      case '1day':
        return new Date(now.getTime() + 24 * 60 * 60 * 1000);
      case '1week':
        return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      case '1month':
        return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      case 'never':
        return new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000 * 10);
      default:
        return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    }
  };

  const handleCheck = useCallback((selectedColumn: any) => {
    setSelectedColumns((prev) => {
      return prev.find((column) => column.data === selectedColumn.data)
        ? prev.filter((column) => column.data !== selectedColumn.data)
        : [...prev, selectedColumn];
    });
  }, []);

  const baseSettings = getBaseTableSettings();

  const getColumnType = (column: any) => {
    if (INTERNAL_COLUMNS.includes(column.data)) {
      return 'internal';
    }
    return 'public';
  };

  const sharedData = useMemo(() => {
    return {
      selectedFields: selectedColumns,
      clientId: selectedClient?.id,
      projectId: project?.id || '',
      companyId: project?.companyId || '',
      expireDate: getExpirationDate(selectedExpirationDate),
    };
  }, [selectedColumns, selectedClient, project?.id, project?.companyId, selectedExpirationDate]);

  const handleCreateShareLink = useCallback(async () => {
    try {
      addNotification('info', 'Creating share link...');
      const response = await createShareLink(sharedData);
      setShareLink(response);
      addNotification('success', 'Share link created successfully!');
    } catch (error) {
      console.error('Error creating share link:', error);
      addNotification('error', 'Failed to create share link. Please try again.');
    }
  }, [sharedData, createShareLink, addNotification]);

  const shareDisabled = useMemo(() => {
    return !selectedClient || !selectedColumns.length || isCreatingShareLink;
  }, [selectedClient, selectedColumns, isCreatingShareLink]);

  const resetState = useCallback(() => {
    setShareLink(null);
    setSelectedClient(null);
    setSelectedExpirationDate('1month');
    setSelectedColumns([]);
  }, []);

  const handleClear = useCallback(() => {
    resetState();
  }, [resetState]);

  const handleOnCancel = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const isColumnChecked = useCallback(
    (column: any) => !!selectedColumns.find((selectedColumn) => selectedColumn.data === column.data),
    [selectedColumns],
  );

  const areAllColumnsSelected = useMemo(() => {
    return columns.length > 0 && selectedColumns.length === columns.length;
  }, [columns, selectedColumns]);

  const toggleSelectAllColumns = useCallback(() => {
    if (areAllColumnsSelected) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(columns);
    }
  }, [areAllColumnsSelected, columns]);

  useEffect(() => {
    if (columns.length > 0 && isOpen) {
      setSelectedColumns(columns);
    }
  }, [columns, isOpen]);

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={handleOnCancel}
      footer={
        <Footer
          handleOnCreateShareLink={handleCreateShareLink}
          handleClear={handleClear}
          shareLink={shareLink}
          isCreatingShareLink={isCreatingShareLink}
          disabled={shareDisabled}
        />
      }
      header={
        <div className="flex-shrink-0 px-6">
          <div className="flex flex-col gap-2">
            <h1 className="text-xl font-semibold text-gray-900">Share Container Data</h1>
            <p className="text-sm text-gray-600">Select client and columns to share the data and preview the result.</p>
          </div>
        </div>
      }
      width="w-3/4"
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 flex flex-col lg:flex-row gap-6 p-6 min-h-0">
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            <div className="flex items-center gap-2">
              <EyeIcon className="w-5 h-5 text-blue-600" />
              <h2 className="font-medium text-gray-900">Preview</h2>
            </div>
            <div className="flex-1 min-h-0 border border-gray-200 rounded-lg overflow-hidden">
              {selectedColumns.length > 0 ? (
                <div className="h-full">
                  <HotTable
                    data={(tableData ?? []).filter((row: any) => row.client === selectedClient?.name)}
                    columns={selectedColumns.map((column) => ({ ...column, renderer: 'text' }))}
                    readOnly={true}
                    {...baseSettings}
                    licenseKey="non-commercial-and-evaluation"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full bg-gray-50">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">No columns selected</p>
                    <p className="text-xs text-gray-400 mt-1">Select columns from the right panel to see preview</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-6 w-full lg:w-80 xl:w-96 flex-shrink-0">
            <div className="space-y-3 ">
              <label className="block text-sm font-medium text-gray-900">
                Select Client <span className="text-red-600 ml-1">*</span>
              </label>
              <ClientSelect
                clients={clientCompanies}
                selectedClientId={selectedClient?.id}
                setSelectedClient={setSelectedClient}
                isLoading={isLoadingClients}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-900">Select Columns</label>
                {selectedColumns.length > 0 && (
                  <div className="text-xs text-blue-600 bg-blue-50 rounded-md px-2 py-1">
                    {selectedColumns.length} column
                    {selectedColumns.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
              <div className="pb-1 border-b border-gray-200">
                <label
                  htmlFor="select-all-columns"
                  className="flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-100"
                >
                  <input
                    id="select-all-columns"
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    checked={areAllColumnsSelected}
                    onChange={toggleSelectAllColumns}
                  />
                  <span className="flex-1 text-[13px] font-medium text-gray-700">Select all</span>
                </label>
              </div>
              <div className="relative">
                <div className="max-h-64 overflow-y-auto p-3 space-y-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  {columns.map((column: any, index: number) => (
                    <ColumnCheckbox
                      key={`${column.key}-${index}`}
                      column={column}
                      type={getColumnType(column)}
                      onCheck={handleCheck}
                      isChecked={isColumnChecked(column)}
                    />
                  ))}
                </div>

                <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-white to-transparent pointer-events-none z-10" />

                <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent pointer-events-none z-10" />
              </div>
            </div>
            <div className="space-y-3 pb-3">
              <label className="block text-sm font-medium text-gray-900">Select share link expiration time</label>
              <ExpirationDateSelect
                selectedExpiration={selectedExpirationDate}
                setSelectedExpiration={setSelectedExpirationDate}
              />
            </div>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default ShareTableWizard;
